import { ageRecipientsFile, generateAgeIdentity } from "./age";
import { generateNaclKeypair } from "./nacl";
import type { Jwk, JwkSet, KeyPair, KeyResult } from "./types";
import { generateWireguardConfigs } from "./wireguard";

export async function wireguardResult(serverPrivateKey: string): Promise<KeyResult> {
  const { server, client } = await generateWireguardConfigs(serverPrivateKey);
  return {
    outputs: [
      { label: "Server configuration", value: server, rows: 5 },
      { label: "Client configuration", value: client, rows: 5 },
    ],
    fingerprint: "",
  };
}

// Named the way `/cryptography`'s NaCl box family names them, that being where a pair minted here is going: the
// secret half is one side's own and the public one is what the other side is handed to seal a message to it.
export async function naclResult(format: string): Promise<KeyResult> {
  const { secretKey, publicKey } = await generateNaclKeypair(format);
  return {
    outputs: [
      { label: "Secret key", value: secretKey, rows: 2 },
      { label: "Public key", value: publicKey, rows: 2 },
    ],
    fingerprint: "",
  };
}

// The recipient is a box of its own rather than the line beside the heading a fingerprint gets: it is the half that
// is handed out, and what somebody came here to copy.
export async function ageResult(postQuantum: boolean): Promise<KeyResult> {
  const { file, recipient } = await generateAgeIdentity(postQuantum);
  return {
    outputs: [
      { label: "Identity file", value: file, rows: 3 },
      // A post-quantum recipient runs to two thousand characters, which is a box that has to be able to grow.
      { label: "Recipient", value: recipient, rows: 2 },
    ],
    fingerprint: "",
  };
}

// `age-keygen -y` writes one file and not two: the identity it was handed is already on screen in the box above.
export async function ageRecipientsResult(identityFile: string): Promise<KeyResult> {
  return { outputs: [{ label: "Recipients", value: await ageRecipientsFile(identityFile), rows: 2 }], fingerprint: "" };
}

export function jwkResult({ privateKeys, publicKeys, thumbprint }: JwkSet): KeyResult {
  // An HMAC key has no half to hand out, so the one box is the key itself rather than the private one.
  const owned = publicKeys.length === 0 ? "Key" : "Private key";
  const many = privateKeys.length > 1;
  const outputs = [{ label: many ? `${owned} set` : owned, value: writeJwks(privateKeys), rows: 8 }];
  if (publicKeys.length > 0) {
    outputs.push({ label: many ? "Public key set" : "Public key", value: writeJwks(publicKeys), rows: 8 });
  }
  return { outputs, fingerprint: thumbprint };
}

// One key is written on its own; more than one is the JWK Set an endpoint serves, which is a list under `keys`.
export function writeJwks(keys: Jwk[]): string {
  return JSON.stringify(keys.length === 1 ? keys[0] : { keys }, null, 2);
}

export function pairResult(kind: string, pair: KeyPair): KeyResult {
  return {
    outputs: [
      { label: "Private key", value: pair.privateKey, rows: 6 },
      // An SSH public key is a single line; a PGP one is a block like the private half above it.
      { label: "Public key", value: pair.publicKey, rows: kind === "ssh" ? 2 : 6 },
    ],
    fingerprint: pair.fingerprint,
  };
}
